import React, { useCallback, useEffect, useState, useMemo } from "react";
import confetti from "canvas-confetti";
import ReactFlow, {
  addEdge,
  ConnectionLineType,
  Background,
  MiniMap,
  useReactFlow,
  useOnSelectionChange,
} from "reactflow";
import "reactflow/dist/style.css";
import { motion, AnimatePresence } from "framer-motion";
import ResponseNode from "./ResponseNode";
import InputField from "./InputField";
import ProgressiveAchievement from "./ProgressiveAchievement";
import ScreenCapture from "./ScreenCapture";
import RotatedBookIcon from "./RotatedBookIcon";
import ConditionalFeature from "./ConditionalFeature";
import VideoDemo from "./VideoDemo";
import TransformDemoFlow from "./TransformDemoFlow";
import FounderStory from "./FounderStory";
import HowItWorks from "./HowItWorks";
import Footer from "./Footer";
import { useFlowContext } from "../hooks/useFlowContext";
import useFeatureFlags from "../hooks/useFeatureFlags";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../hooks/useSubscription";
import LogoutConfirmModal from "./LogoutConfirmModal";
import LoginModal from "./LoginModal";
import OnboardingFlow from "./OnboardingFlow";
import TransitionScreen from "./TransitionScreen";
import PaywallModal from "./PaywallModal";
import SuccessModal from "./SuccessModal";
import RateLimitModal from "./RateLimitModal";
import {
  BookOpen,
  Sparkles,
  FolderOpen,
  X,
  Trash2,
  Minimize2,
  Maximize2,
  Scan,
  Lock,
  Search,
  Grid3X3,
  List,
  Calendar,
  TrendingUp,
  Star,
  Filter,
  ChevronDown,
  Plus,
  Minus,
  BookPlus,
  Map,
  Camera,
  LogOut,
  LogIn,
  User,
} from "lucide-react";
import storageService from "../services/storageService";
import {
  getAllWorkflowsFromFirestore,
  loadWorkflowFromFirestore,
  deleteWorkflowFromFirestore,
} from "../services/firestoreService";
import { doc, getDoc, setDoc, getDocFromServer } from "firebase/firestore";
import { db } from "../firebase/config";
import {
  getHeadline,
  getSubheadline,
  getJourneyCardsArray,
} from "../utils/messagingSelector";

const nodeTypes = {
  responseNode: ResponseNode,
};

// Custom Controls Component that can use useReactFlow
const CustomControls = ({
  showStats,
  collapseAllNodes,
  expandAllNodes,
  getExpandedNodesCount,
  nodes,
  featureFlags,
  edgeType,
  setEdgeType,
}) => {
  const { zoomIn, zoomOut, fitView, zoomTo, setCenter } = useReactFlow();
  const [showScreenCapture, setShowScreenCapture] = React.useState(false);
  const [showEdgeTypeDropdown, setShowEdgeTypeDropdown] = React.useState(false);

  // Determine if we should show expand or collapse button
  const expandedCount = getExpandedNodesCount();
  const expandableNodes = nodes.filter(
    (node) =>
      node.type === "responseNode" &&
      node.data?.response &&
      node.data.response.split("\n").length > 5
  );
  const isAllExpanded =
    expandedCount === expandableNodes.length && expandableNodes.length > 0;

  const handleToggleExpandCollapse = () => {
    if (isAllExpanded) {
      collapseAllNodes();
    } else {
      expandAllNodes();
    }
  };

  const handleScreenshotClick = () => {
    setShowScreenCapture(true);
  };

  // Edge type options with visual previews
  const edgeTypeOptions = [
    {
      value: "default",
      label: "Bezier",
      description: "Smooth curved line",
      icon: "〜",
    },
    {
      value: "straight",
      label: "Straight",
      description: "Direct line",
      icon: "─",
    },
    {
      value: "step",
      label: "Step",
      description: "Right angles",
      icon: "└─",
    },
    {
      value: "smoothstep",
      label: "Smooth Step",
      description: "Rounded angles",
      icon: "╰─",
    },
    {
      value: "simplebezier",
      label: "Simple Bezier",
      description: "Simple curve",
      icon: "⌒",
    },
  ];

  const currentEdgeType =
    edgeTypeOptions.find((option) => option.value === edgeType) ||
    edgeTypeOptions[3];

  return (
    <>
      <div
        className="fixed top-16 right-2 z-50 flex items-center gap-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl px-3 py-2 shadow-lg"
        style={{
          right: "8px",
        }}>
        {expandableNodes.length > 0 && (
          <>
            <button
              onClick={handleToggleExpandCollapse}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title={isAllExpanded ? "Collapse All" : "Expand All"}>
              {isAllExpanded ? (
                <Minimize2 size={16} className="text-gray-600" />
              ) : (
                <Maximize2 size={16} className="text-gray-600" />
              )}
            </button>
            <div className="w-px h-6 bg-gray-300"></div>
          </>
        )}
        {featureFlags.screenCapture && (
          <>
            <button
              onClick={handleScreenshotClick}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Screenshot">
              <Camera size={16} className="text-gray-600" />
            </button>
            <div className="w-px h-6 bg-gray-300"></div>
          </>
        )}
        {featureFlags.edgeTypeSelector && (
          <>
            <div className="relative">
              <button
                onClick={() => setShowEdgeTypeDropdown(!showEdgeTypeDropdown)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1"
                title="Connection Line Type">
                <span className="text-gray-600 text-sm font-medium">
                  {currentEdgeType.icon}
                </span>
                <ChevronDown size={12} className="text-gray-600" />
              </button>

              {showEdgeTypeDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowEdgeTypeDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    {edgeTypeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setEdgeType(option.value);
                          setShowEdgeTypeDropdown(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                          edgeType === option.value ? "bg-blue-50" : ""
                        }`}>
                        <span className="text-lg w-6 text-center">
                          {option.icon}
                        </span>
                        <div className="flex-1">
                          <div
                            className={`text-sm font-medium ${
                              edgeType === option.value
                                ? "text-blue-600"
                                : "text-gray-700"
                            }`}>
                            {option.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {option.description}
                          </div>
                        </div>
                        {edgeType === option.value && (
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="w-px h-6 bg-gray-300"></div>
          </>
        )}
        <button
          onClick={() => zoomIn()}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Zoom In">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-600">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
            <line x1="8" y1="11" x2="14" y2="11" />
            <line x1="11" y1="8" x2="11" y2="14" />
          </svg>
        </button>
        <div className="w-px h-6 bg-gray-300"></div>
        <button
          onClick={() => zoomOut()}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Zoom Out">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-600">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <div className="w-px h-6 bg-gray-300"></div>
        <button
          onClick={() => {
            // Get all visible nodes (not hidden)
            const visibleNodes = nodes?.filter((node) => !node.hidden) || [];

            if (visibleNodes.length <= 1) {
              // For 1 or fewer nodes, zoom to maximum and center on the node
              zoomTo(1.0, { duration: 800 }); // Force maximum zoom level
              if (visibleNodes.length === 1) {
                // Center on the single node
                const node = visibleNodes[0];
                setCenter(node.position.x, node.position.y, {
                  duration: 800,
                  zoom: 1.0,
                });
              }
            } else if (visibleNodes.length > 1) {
              // For multiple nodes, fit to visible nodes with tighter zoom constraints
              fitView({
                padding: 0.2,
                duration: 800,
                nodes: visibleNodes,
                minZoom: 0.1,
                maxZoom: 2,
              });
            } else {
              // If no visible nodes, reset to default view
              fitView({
                padding: 0.2,
                duration: 800,
                minZoom: 0.5,
                maxZoom: 1.5,
              });
            }
          }}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title="Fit View">
          <Scan size={16} className="text-gray-600" />
        </button>
      </div>

      {featureFlags.screenCapture && showScreenCapture && (
        <ScreenCapture
          mode="window"
          onClose={() => {
            setShowScreenCapture(false);
          }}
        />
      )}
    </>
  );
};

const FlowCanvas = () => {
  const { user, isAuthenticated, logout, login } = useAuth();
  const { hasPremiumAccess, loading: subscriptionLoading, createCheckoutSession } = useSubscription();
  
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  
  // Onboarding and Paywall states
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [userPersonality, setUserPersonality] = useState(null); // Store user's personality type
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    nodeCount,
    isLoading,
    error,
    rateLimitError,
    clearRateLimitError,
    subscriptionError,
    clearSubscriptionError,
    handleQuestionSubmit,
    interactionCount,
    setReactFlowInstance,
    collapseAllNodes,
    expandAllNodes,
    getExpandedNodesCount,
    bringNodeToFront,
    biblicalMetrics,
    onViewportChange,
    currentWorkflowName,
    setCurrentWorkflowName,
    currentFirestoreSessionId,
    isLoadingWorkflow,
    loadWorkflow,
    resetWorkflow,
    edgeType,
    setEdgeType,
    // Recovery management
    showRecoveryPrompt,
    recoveryData,
    recoverAutosave,
    dismissRecovery,
    transferWorkflowOwnership,
  } = useFlowContext();

  // Memoize books and verses calculation to prevent unnecessary re-renders
  const { booksCount, versesCount, books, verses } = useMemo(() => {
    const allBooks = new Set();
    const allVerses = new Set();

    nodes.forEach((node) => {
      if (node.data?.booksMentioned) {
        node.data.booksMentioned.forEach((book) => allBooks.add(book));
      }
      if (node.data?.versesReferenced) {
        node.data.versesReferenced.forEach((verse) => allVerses.add(verse));
      }
    });

    return {
      booksCount: allBooks.size,
      versesCount: allVerses.size,
      books: Array.from(allBooks),
      verses: Array.from(allVerses),
    };
  }, [nodes]);

  const [showWelcome, setShowWelcome] = useState(true);
  const [showStats, setShowStats] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showFirstQuestionCelebration, setShowFirstQuestionCelebration] =
    useState(false);
  const [hasShownFirstCelebration, setHasShownFirstCelebration] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // 'recent', 'name', 'books', 'verses'
  const [viewMode, setViewMode] = useState("list"); // 'list', 'grid'
  const [showNewConfirmDialog, setShowNewConfirmDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null); // { id, name }
  const [isMinimapVisible, setIsMinimapVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isVideoRecorderOpen, setIsVideoRecorderOpen] = useState(false);
  const [localEdgeType, setLocalEdgeType] = useState("default"); // 'default' = Bezier curve
  const [currentMessagingVersion, setCurrentMessagingVersion] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("version") || "22";
  });

  // Force component to re-read messaging when version changes
  const [, forceUpdate] = useState({});

  // Feature flags
  const featureFlags = useFeatureFlags();

  // Sync local edge type with context edge type when workflow loads or changes
  useEffect(() => {
    if (edgeType && edgeType !== localEdgeType) {
      console.log("🔄 Syncing localEdgeType from context:", edgeType);
      setLocalEdgeType(edgeType);
    }
  }, [edgeType]);

  // Update context edge type when local changes (debounced to avoid loops)
  useEffect(() => {
    if (localEdgeType && localEdgeType !== edgeType) {
      console.log("🔄 Updating context edgeType from local:", localEdgeType);
      setEdgeType(localEdgeType);
    }
  }, [localEdgeType]);

  // Apply edge type to all edges using useMemo to avoid infinite loops
  const edgesWithType = useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      type: localEdgeType,
    }));
  }, [edges, localEdgeType]);

  // Reset sort option if progress feature is disabled
  useEffect(() => {
    if (
      !featureFlags.biblicalExplorationProgress &&
      (sortBy === "books" || sortBy === "verses")
    ) {
      setSortBy("recent");
    }
  }, [featureFlags.biblicalExplorationProgress, sortBy]);

  // Keyboard shortcuts for version switching (Ctrl + Arrow Keys)
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only trigger if Ctrl/Cmd is pressed with arrow keys
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "ArrowRight" || e.key === "ArrowLeft")
      ) {
        e.preventDefault(); // Prevent browser navigation

        let newVersion = parseInt(currentMessagingVersion);

        if (e.key === "ArrowRight") {
          // Next version (cycle back to 1 after 22)
          newVersion = newVersion >= 22 ? 1 : newVersion + 1;
          console.log(`⏭️  Next version: ${newVersion}`);
        } else if (e.key === "ArrowLeft") {
          // Previous version (cycle back to 22 from 1)
          newVersion = newVersion <= 1 ? 22 : newVersion - 1;
          console.log(`⏮️  Previous version: ${newVersion}`);
        }

        // Update URL without reload using pushState
        const newUrl = `${window.location.pathname}?version=${newVersion}`;
        window.history.pushState({}, "", newUrl);

        // Update state to trigger re-render
        setCurrentMessagingVersion(newVersion.toString());

        // Force re-render to update all messaging
        forceUpdate({});
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentMessagingVersion]);

  // Dynamic video recording components
  const [VideoRecordingTrigger, setVideoRecordingTrigger] = useState(null);
  const [VideoStorageTrigger, setVideoStorageTrigger] = useState(null);
  const [VideoRecorder, setVideoRecorder] = useState(null);

  // Biblical book categorization
  const oldTestamentBooks = [
    "Genesis",
    "Exodus",
    "Leviticus",
    "Numbers",
    "Deuteronomy",
    "Joshua",
    "Judges",
    "Ruth",
    "1 Samuel",
    "2 Samuel",
    "1 Kings",
    "2 Kings",
    "1 Chronicles",
    "2 Chronicles",
    "Ezra",
    "Nehemiah",
    "Esther",
    "Job",
    "Psalms",
    "Proverbs",
    "Ecclesiastes",
    "Song of Solomon",
    "Isaiah",
    "Jeremiah",
    "Lamentations",
    "Ezekiel",
    "Daniel",
    "Hosea",
    "Joel",
    "Amos",
    "Obadiah",
    "Jonah",
    "Micah",
    "Nahum",
    "Habakkuk",
    "Zephaniah",
    "Haggai",
    "Zechariah",
    "Malachi",
  ];

  const newTestamentBooks = [
    "Matthew",
    "Mark",
    "Luke",
    "John",
    "Acts",
    "Romans",
    "1 Corinthians",
    "2 Corinthians",
    "Galatians",
    "Ephesians",
    "Philippians",
    "Colossians",
    "1 Thessalonians",
    "2 Thessalonians",
    "1 Timothy",
    "2 Timothy",
    "Titus",
    "Philemon",
    "Hebrews",
    "James",
    "1 Peter",
    "2 Peter",
    "1 John",
    "2 John",
    "3 John",
    "Jude",
    "Revelation",
  ];

  // Sort books in biblical order
  const sortBooksBiblically = (booksList, referenceOrder) => {
    return booksList.sort((a, b) => {
      const indexA = referenceOrder.indexOf(a);
      const indexB = referenceOrder.indexOf(b);

      // If both books are found in reference order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is found, prioritize the one in reference order
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither is found, sort alphabetically as fallback
      return a.localeCompare(b);
    });
  };

  // Categorize explored books
  const categorizeBooks = (booksList) => {
    const oldTestament = booksList.filter((book) =>
      oldTestamentBooks.includes(book)
    );
    const newTestament = booksList.filter((book) =>
      newTestamentBooks.includes(book)
    );
    const other = booksList.filter(
      (book) =>
        !oldTestamentBooks.includes(book) && !newTestamentBooks.includes(book)
    );

    return {
      oldTestament: sortBooksBiblically(oldTestament, oldTestamentBooks),
      newTestament: sortBooksBiblically(newTestament, newTestamentBooks),
      other: other.sort(), // Keep alphabetical for unknown books
    };
  };

  // ResizeObserver error handling - prevents hanging after workflow load
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args) => {
      const message = args[0]?.toString?.() || "";
      if (
        message.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        message.includes("ResizeObserver loop limit exceeded") ||
        message.includes("ResizeObserver")
      ) {
        return; // Completely suppress these errors
      }
      originalError.apply(console, args);
    };

    console.warn = (...args) => {
      const message = args[0]?.toString?.() || "";
      if (message.includes("ResizeObserver")) {
        return;
      }
      originalWarn.apply(console, args);
    };

    // Handle runtime errors via window.onerror
    const originalWindowError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (message?.toString?.().includes("ResizeObserver")) {
        return true; // Prevent default error handling
      }
      if (originalWindowError) {
        return originalWindowError(message, source, lineno, colno, error);
      }
      return false;
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      if (event.reason?.message?.includes?.("ResizeObserver")) {
        event.preventDefault();
      }
    };

    // Handle error events
    const handleError = (event) => {
      if (
        event.error?.message?.includes?.("ResizeObserver") ||
        event.message?.includes?.("ResizeObserver")
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    // Hide webpack overlay errors with retry mechanism
    const hideOverlays = () => {
      const overlayDiv = document.getElementById(
        "webpack-dev-server-client-overlay-div"
      );
      const overlay = document.getElementById(
        "webpack-dev-server-client-overlay"
      );
      const errorOverlay = document.querySelector(
        '[data-test-id="error-overlay"]'
      );

      if (overlayDiv) overlayDiv.style.display = "none";
      if (overlay) overlay.style.display = "none";
      if (errorOverlay) errorOverlay.style.display = "none";
    };

    hideOverlays();
    const hideTimer = setInterval(hideOverlays, 100);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.onerror = originalWindowError;
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleError);
      clearInterval(hideTimer);
    };
  }, []);

  // Load UI preferences and handle mobile detection
  useEffect(() => {
    const initializeUIPreferences = async () => {
      try {
        const preferences = await storageService.getUIPreferences();
        const mobile = window.innerWidth < 640; // sm breakpoint
        setIsMobile(mobile);

        // Load saved preferences, but respect mobile defaults
        setIsMinimapVisible(mobile ? false : preferences.isMinimapVisible);
        setSortBy(preferences.searchOptions.sortBy);
        setViewMode(preferences.searchOptions.viewMode);
      } catch (error) {
        console.error("Failed to load UI preferences:", error);
        // Fallback to defaults
        const mobile = window.innerWidth < 640;
        setIsMobile(mobile);
        setIsMinimapVisible(!mobile);
      }
    };

    const checkMobile = () => {
      const mobile = window.innerWidth < 640; // sm breakpoint
      setIsMobile(mobile);
      // On mobile changes, hide minimap but don't override saved preference for desktop
      if (mobile && isMinimapVisible) {
        setIsMinimapVisible(false);
      }
    };

    initializeUIPreferences();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  // Check onboarding and subscription status after login
  useEffect(() => {
    console.log('🚀 Onboarding useEffect triggered', {
      isAuthenticated,
      userId: user?.id,
      subscriptionLoading,
      hasPremiumAccess
    });

    // Reset checkout states when authentication changes (safety cleanup)
    if (!isAuthenticated) {
      setCheckoutLoading(false);
      setIsProcessingCheckout(false);
    }

    // Debug flag: force onboarding via URL ?debugOnboarding=1
    const urlParams = new URLSearchParams(window.location.search);
    const forceOnboarding = urlParams.get('debugOnboarding') === '1';

    if (forceOnboarding) {
      console.log('⚡ Force onboarding via URL parameter');
    }

    // Wait for subscription check to complete before checking onboarding
    if (subscriptionLoading) {
      console.log('⏳ Waiting for subscription check to complete...');
      return;
    }

    if (isAuthenticated && user?.id) {
      console.log('✅ User authenticated, checking onboarding status...');

      const checkUserStatus = async () => {
        try {
          // Check if user document exists in Firestore
          // Force fresh read from server (bypass cache)
          const userDocRef = doc(db, 'users', user.id);

          console.log('🔍 Reading from Firestore path:', `users/${user.id}`);

          // Clear any potential cache first
          console.log('🧹 Attempting to clear Firestore cache...');

          const userDoc = await getDocFromServer(userDocRef);

          console.log('🔍 Document metadata (forced from server):', {
            fromCache: userDoc.metadata.fromCache,
            hasPendingWrites: userDoc.metadata.hasPendingWrites,
            documentPath: userDoc.ref.path
          });

          const userData = userDoc.data();

          // Log ONLY the actual fields that exist in Firestore
          console.log('📄 ACTUAL Firestore fields:', Object.keys(userData || {}));
          console.log('📄 Full document data:', JSON.parse(JSON.stringify(userData || {})));

          // Check specific fields
          console.log('📄 Field checks:', {
            exists: userDoc.exists(),
            hasCompletedOnboarding: userData?.hasCompletedOnboarding,
            hasCompletedOnboardingExists: 'hasCompletedOnboarding' in (userData || {}),
            onboardingCompletedAt: userData?.onboardingCompletedAt,
            onboardingCompletedAtExists: 'onboardingCompletedAt' in (userData || {}),
            allFields: userData
          });

          const hasValidOnboardingCompletion =
            userData?.hasCompletedOnboarding === true &&
            userData?.onboardingCompletedAt &&
            typeof userData.onboardingCompletedAt === 'string';

          console.log('📄 User document check:', {
            exists: userDoc.exists(),
            forceOnboarding,
            hasValidOnboardingCompletion
          });

          // Show onboarding if:
          // 1. User document doesn't exist, OR
          // 2. User hasn't completed onboarding (no flag or no timestamp), OR
          // 3. Debug flag is set
          if (!userDoc.exists() || !hasValidOnboardingCompletion || forceOnboarding) {
            console.log('🎯 SHOWING ONBOARDING for user:', user.id, {
              reason: !userDoc.exists() ? 'No user doc' :
                      !hasValidOnboardingCompletion ? 'Invalid/missing onboarding completion' :
                      'Force onboarding flag'
            });
            setShowOnboarding(true);
          } else {
            console.log('✔️ User has completed onboarding (valid timestamp found)');
            // Existing user who completed onboarding
            setHasCompletedOnboarding(true);

            // Check if they have subscription
            if (!hasPremiumAccess && !isProcessingCheckout && !showSuccessModal) {
              console.log('💰 No subscription - showing transition letter');
              // No subscription - show transition letter (they can go back to onboarding if needed)
              setShowTransition(true);
            } else {
              console.log('👑 User has premium access - skipping all modals');
            }
          }
        } catch (error) {
          console.error('❌ Error checking user status:', error);
          // On error, show onboarding to be safe
          setShowOnboarding(true);
        }
      };

      checkUserStatus();
    } else {
      console.log('⚠️ Not authenticated or no user ID');
    }
  }, [isAuthenticated, subscriptionLoading, hasPremiumAccess, user, isProcessingCheckout, showSuccessModal]);

  // Close paywall and transition screen when subscription becomes active
  useEffect(() => {
    if (hasPremiumAccess && (showPaywall || showTransition)) {
      console.log('✅ FlowCanvas: Subscription detected, closing paywall and transition');
      setShowPaywall(false);
      setShowTransition(false);
    }
  }, [hasPremiumAccess, showPaywall, showTransition]);

  // Show paywall when subscription error is detected
  useEffect(() => {
    if (subscriptionError && isAuthenticated) {
      console.log('🔒 FlowCanvas: Subscription error detected, showing paywall');
      setShowPaywall(true);
      // Clear the subscription error after showing paywall
      clearSubscriptionError();
    }
  }, [subscriptionError, isAuthenticated, clearSubscriptionError]);

  // Reset checkout loading state when user returns from Stripe (via back button)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && (checkoutLoading || isProcessingCheckout)) {
        console.log('👀 User returned to page - resetting checkout states');
        // User returned to the page (probably from Stripe via back button)
        // Reset loading states after a short delay to ensure they actually came back
        setTimeout(() => {
          setCheckoutLoading(false);
          setIsProcessingCheckout(false);
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkoutLoading, isProcessingCheckout]);

// Auto-open paywall effect removed – onboarding now controls paywall visibility

  // Handle payment success/cancel from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');

    if (paymentStatus === 'success') {
      // Payment successful - close all modals and show success modal
      setShowPaywall(false);
      setShowTransition(false);
      setShowOnboarding(false);
      setShowSuccessModal(true);
      // Clean URL
      window.history.replaceState({}, '', '/');
    } else if (paymentStatus === 'cancelled') {
      // User cancelled payment - keep paywall open
      alert('Payment cancelled. Subscribe to start exploring.');
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Handle onboarding completion
  const handleOnboardingComplete = async (selections) => {
    console.log('🎯 handleOnboardingComplete CALLED');
    console.log('🎯 User data:', { id: user?.id, email: user?.email, name: user?.name });
    console.log('🎯 Onboarding selections:', selections);

    try {
      // Save onboarding completion to Firestore
      const userDocRef = doc(db, 'users', user.id);

      const dataToSave = {
        hasCompletedOnboarding: true,
        onboardingCompletedAt: new Date().toISOString(),
        email: user.email,
        name: user.name,
        userId: user.id,
        // Add onboarding selections including personality
        personality: selections?.personality || null,
        intent: selections?.intent || null,  // Why they came (questions/guidance/study/explore)
        experience: selections?.experience || null,  // Where they are (beginner/intermediate/advanced)
        behavior: selections?.behavior || null  // How they read (apply/context/share/reflect)
      };

      console.log('💾 Attempting to save to Firestore:', {
        path: `users/${user.id}`,
        data: dataToSave
      });

      await setDoc(userDocRef, dataToSave, { merge: true });

      console.log('✅ Successfully saved to Firestore!');

      // Store personality in state for TransitionScreen
      setUserPersonality(selections?.personality || null);
      setHasCompletedOnboarding(true);
      setShowOnboarding(false);

      // Show transition screen before paywall
      setShowTransition(true);
    } catch (error) {
      console.error('❌ Error saving onboarding completion:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Still proceed even if save fails - show transition screen
      setHasCompletedOnboarding(true);
      setShowOnboarding(false);
      setShowTransition(true);
    }
  };

  const handleOnboardingSkip = () => {
    // Close onboarding without saving - return to landing page
    setShowOnboarding(false);
    // Don't set hasCompletedOnboarding or show paywall
    // User can explore and come back to onboarding later
  };

  // Handle transition screen completion
  const handleTransitionComplete = () => {
    setShowTransition(false);

    // Check subscription status and show paywall if needed
    if (!hasPremiumAccess) {
      setShowPaywall(true);
    }
  };

  // Handle back from transition screen
  const handleTransitionBack = () => {
    setShowTransition(false);
    setShowOnboarding(true);
  };

  // Handle back from paywall
  const handlePaywallBack = () => {
    setShowPaywall(false);
    setShowTransition(true);
  };

  // Handle subscription
  const handleSubscribe = async (priceId, billingCycle) => {
    try {
      // Set checkout flag to prevent useEffect from showing other modals
      setIsProcessingCheckout(true);
      setCheckoutLoading(true);

      // Keep paywall open - it will show loading state
      // Don't close modals until redirect happens

      // Create Stripe checkout session
      const checkoutUrl = await createCheckoutSession(priceId, billingCycle);

      // Redirect to Stripe Checkout (paywall stays open until redirect completes)
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
      setIsProcessingCheckout(false);
    }
  };

  // Wrapper for handleQuestionSubmit that checks for premium access
  const handleQuestionSubmitWithCheck = useCallback((question) => {
    // Check if user has premium access
    if (!hasPremiumAccess && isAuthenticated) {
      // Show paywall if user tries to submit without subscription
      console.log('🔒 Feature blocked: User needs subscription');
      setShowPaywall(true);
      return;
    }
    
    // If user has premium access or not authenticated (will be handled by InputField), proceed
    handleQuestionSubmit(question);
  }, [hasPremiumAccess, isAuthenticated, handleQuestionSubmit]);

  // Wrapper for loadWorkflow that checks for premium access
  const loadWorkflowWithCheck = useCallback((workflow) => {
    // Check if user has premium access
    if (!hasPremiumAccess && isAuthenticated) {
      console.log('🔒 Default workflow loading blocked: Subscription required');
      setShowPaywall(true);
      return;
    }
    
    // If user has premium access or not authenticated, proceed
    loadWorkflow(workflow);
  }, [hasPremiumAccess, isAuthenticated, loadWorkflow]);

  // Memoize display metrics to prevent unnecessary re-calculations
  const displayConfig = useMemo(() => {
    if (interactionCount < 3) {
      return { showTopics: false, showDepth: false };
    } else if (interactionCount < 8) {
      return { showTopics: true, showDepth: false };
    } else {
      return { showTopics: true, showDepth: true };
    }
  }, [interactionCount]);

  const onConnect = useCallback(
    (params) =>
      onEdgesChange(
        addEdge({ ...params, animated: true, type: localEdgeType }, edges)
      ),
    [edges, onEdgesChange, localEdgeType]
  );

  // Debug function to log current node states
  const logNodeStates = useCallback(() => {
    console.log("=== CURRENT NODE STATES ===");
    nodes.forEach((node) => {
      if (node.type === "responseNode") {
        console.log(
          `Node ${node.id}: zIndex=${node.zIndex || "undefined"}, position=(${
            node.position.x
          }, ${node.position.y})`
        );
      }
    });
    console.log("=============================");
  }, [nodes]);

  // Handle node selection to bring clicked node to front
  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes }) => {
      if (selectedNodes && selectedNodes.length === 1) {
        const selectedNode = selectedNodes[0];
        // Only bring response nodes to front (ignore other node types)
        if (selectedNode.type === "responseNode") {
          bringNodeToFront(selectedNode.id);
        }
      }
    },
    [bringNodeToFront]
  );

  // Handle direct node clicks (more reliable than selection)
  const onNodeClick = useCallback(
    (event, node) => {
      // Only bring to front if NOT clicking on interactive elements
      // This prevents parent node from stealing z-index when creating child nodes
      const target = event.target;
      const isInteractiveElement = target.closest(
        "button, input, textarea, select, a, [contenteditable]"
      );

      if (node.type === "responseNode" && !isInteractiveElement) {
        bringNodeToFront(node.id);
      }
    },
    [bringNodeToFront]
  );

  // Log node changes for debugging z-index issues
  useEffect(() => {
    console.log("📊 NODES CHANGED - Current z-index states:");
    nodes.forEach((node) => {
      if (node.type === "responseNode") {
        console.log(`   Node ${node.id}: zIndex=${node.zIndex || "undefined"}`);
      }
    });
  }, [nodes]);

  // Add global debug functions for manual testing
  useEffect(() => {
    window.debugNodes = {
      logStates: logNodeStates,
      bringToFront: (nodeId) => {
        console.log("🔧 Manual bringToFront called for:", nodeId);
        bringNodeToFront(nodeId);
      },
      listNodes: () => {
        const responseNodes = nodes.filter((n) => n.type === "responseNode");
        console.log(
          "Available response nodes:",
          responseNodes.map((n) => ({ id: n.id, zIndex: n.zIndex }))
        );
        return responseNodes.map((n) => n.id);
      },
    };

    return () => {
      delete window.debugNodes;
    };
  }, [logNodeStates, bringNodeToFront, nodes]);

  // Show stats panel when there are nodes (memoized to prevent unnecessary updates)
  useEffect(() => {
    const shouldShowStats = nodeCount > 0;
    setShowStats((prev) => (prev !== shouldShowStats ? shouldShowStats : prev));
  }, [nodeCount]);

  // Hide welcome after first interaction (debounced)
  useEffect(() => {
    if (nodeCount > 0 && showWelcome) {
      const timer = setTimeout(() => setShowWelcome(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [nodeCount, showWelcome]);

  // Show first question celebration with confetti
  useEffect(() => {
    if (
      nodeCount === 1 &&
      !showFirstQuestionCelebration &&
      !hasShownFirstCelebration
    ) {
      // Delay to let the response node appear first
      const timer = setTimeout(() => {
        setShowFirstQuestionCelebration(true);

        // Trigger confetti celebration
        const duration = 3000;
        const end = Date.now() + duration;

        const colors = [
          "#FFD700",
          "#FF6B6B",
          "#4ECDC4",
          "#45B7D1",
          "#96CEB4",
          "#FFEAA7",
        ];

        // Multiple confetti bursts
        const confettiInterval = setInterval(() => {
          if (Date.now() > end) {
            clearInterval(confettiInterval);
            return;
          }

          // Left side burst
          confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 1 },
            colors: colors,
          });

          // Right side burst
          confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 1 },
            colors: colors,
          });

          // Center burst
          confetti({
            particleCount: 100,
            angle: 90,
            spread: 100,
            origin: { x: 0.5, y: 0.3 },
            colors: colors,
          });
        }, 250);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [nodeCount, showFirstQuestionCelebration, hasShownFirstCelebration]);

  // Dynamically load video recording components when feature is enabled
  useEffect(() => {
    if (featureFlags.videoRecording) {
      const loadVideoComponents = async () => {
        try {
          const [
            { default: VideoRecordingTriggerComponent },
            { default: VideoStorageTriggerComponent },
            { default: VideoRecorderComponent },
          ] = await Promise.all([
            import("./VideoRecording/VideoRecordingTrigger"),
            import("./VideoRecording/VideoStorage"),
            import("./VideoRecording/VideoRecorder"),
          ]);

          setVideoRecordingTrigger(() => VideoRecordingTriggerComponent);
          setVideoStorageTrigger(() => VideoStorageTriggerComponent);
          setVideoRecorder(() => VideoRecorderComponent);
        } catch (error) {
          console.error("Failed to load video recording components:", error);
        }
      };

      loadVideoComponents();
    }
  }, [featureFlags.videoRecording]);

  // Handle authentication state changes for ownership transfer
  useEffect(() => {
    // Only attempt transfer if user is authenticated and has nodes to transfer
    if (isAuthenticated && user?.id && nodeCount > 0) {
      // Small delay to ensure all authentication state is settled
      const timer = setTimeout(() => {
        try {
          const result = transferWorkflowOwnership(user.id);

          if (result.transferred) {
            console.log(
              `✅ Successfully transferred ${result.nodeCount} nodes to user ${user.id}`
            );
          } else {
            console.log(`ℹ️ No transfer needed: ${result.reason}`);
          }
        } catch (error) {
          console.error("❌ Failed to transfer workflow ownership:", error);
        }
      }, 1000); // 1 second delay to ensure auth state is stable

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user?.id, nodeCount, transferWorkflowOwnership]);

  // Load workflow handler
  const handleLoadWorkflow = async (workflowId, workflowName) => {
    try {
      // Check premium access before loading
      if (!hasPremiumAccess && isAuthenticated) {
        console.log('🔒 Workflow loading blocked: Subscription required');
        setShowPaywall(true);
        setShowLoadModal(false);
        return;
      }

      console.log("🚀 handleLoadWorkflow called with:", {
        workflowId,
        workflowName,
        currentFirestoreSessionId,
        currentWorkflowName,
      });

      // Determine storage type based on workflow ID format
      // Firestore IDs start with "session-", IndexedDB IDs start with "workflow_"
      const isFirestoreWorkflow = workflowId.startsWith("session-");

      let workflow;
      if (isFirestoreWorkflow && isAuthenticated && user?.id) {
        console.log(`📥 Loading workflow from Firestore: ${workflowId}`);
        workflow = await loadWorkflowFromFirestore(user.id, workflowId);
        if (!workflow) {
          throw new Error("Workflow not found in Firestore");
        }
        console.log(`✅ Loaded workflow:`, {
          sessionId: workflow.sessionId,
          title: workflow.title,
          nodeCount: workflow.nodeCount,
        });
      } else {
        console.log(`📥 Loading workflow from IndexedDB: ${workflowName}`);
        workflow = await storageService.loadWorkflow(workflowName);
      }
      // console.log("📦 Workflow data retrieved:", {
      //   name: workflow.name,
      //   nodeCount: workflow.nodes?.length,
      //   edgeCount: workflow.edges?.length,
      // });

      // Add a small delay before loading to ensure React is ready
      setTimeout(() => {
        loadWorkflow(workflow);

        // For Firestore workflows, only set the session ID (loadWorkflow handles this)
        // For IndexedDB workflows, set the workflow name
        if (!isFirestoreWorkflow) {
          setCurrentWorkflowName(workflowName);
        } else {
          // Clear currentWorkflowName for Firestore workflows
          setCurrentWorkflowName(null);
        }

        setShowLoadModal(false);
      }, 100);
    } catch (error) {
      console.error("Failed to load workflow:", error);
    }
  };

  // Delete workflow handler
  const handleDeleteWorkflow = async (workflowId, workflowName, event) => {
    event.stopPropagation(); // Prevent triggering the load action

    // Prevent deleting the currently active workflow
    // For Firestore: compare session IDs
    // For IndexedDB: compare workflow names
    const isFirestoreWorkflow = workflowId.startsWith("session-");
    const isCurrentlyActive = isFirestoreWorkflow
      ? currentFirestoreSessionId === workflowId
      : currentWorkflowName === workflowName;

    if (isCurrentlyActive) {
      alert(
        "Cannot delete the currently active exploration. Please load a different exploration first."
      );
      return;
    }

    // Show custom delete confirmation dialog
    setWorkflowToDelete({ id: workflowId, name: workflowName });
    setShowDeleteConfirmDialog(true);
  };

  // Confirm delete workflow
  const confirmDeleteWorkflow = async () => {
    if (!workflowToDelete) return;

    console.log("\n🔍 ===== DELETE WORKFLOW DEBUG =====");
    console.log("Workflow to delete:", workflowToDelete);
    console.log("isAuthenticated:", isAuthenticated);
    console.log("user?.id:", user?.id);
    console.log("user object:", user);

    try {
      // Determine storage type based on workflow ID format
      // Firestore IDs start with "session-", IndexedDB IDs start with "workflow_"
      const isFirestoreWorkflow = workflowToDelete.id.startsWith("session-");
      console.log("Is Firestore workflow?", isFirestoreWorkflow);

      // CRITICAL FIX: Delete from BOTH storage locations to prevent orphaned data
      // This ensures complete cleanup regardless of where the exploration was saved

      if (isFirestoreWorkflow && isAuthenticated && user?.id) {
        console.log("🎯 DELETE PATH: Firestore + IndexedDB cleanup");
        console.log(
          `🗑️ Deleting workflow from Firestore: ${workflowToDelete.id}`
        );
        console.log(`   User ID: ${user.id}`);
        console.log(`   Session ID: ${workflowToDelete.id}`);

        // Delete from Firestore
        await deleteWorkflowFromFirestore(user.id, workflowToDelete.id);
        console.log("✅ Firestore delete completed successfully");

        // ALSO delete from IndexedDB by name (in case it exists there too)
        try {
          console.log(
            `🗑️ Also checking IndexedDB for: ${workflowToDelete.name}`
          );
          const indexedDBWorkflows = await storageService.getAllWorkflows();
          const existsInIndexedDB = indexedDBWorkflows.some(
            (w) => w.name === workflowToDelete.name
          );

          if (existsInIndexedDB) {
            await storageService.deleteWorkflow(workflowToDelete.name);
            console.log("✅ Also deleted from IndexedDB");
          } else {
            console.log("ℹ️ Not found in IndexedDB (already clean)");
          }
        } catch (indexedDBError) {
          console.warn(
            "⚠️ IndexedDB cleanup failed (non-critical):",
            indexedDBError
          );
        }
      } else {
        console.log("🎯 DELETE PATH: IndexedDB only");
        console.log(
          `   Reason: isFirestoreWorkflow=${isFirestoreWorkflow}, isAuthenticated=${isAuthenticated}, user?.id=${user?.id}`
        );
        console.log(
          `🗑️ Deleting workflow from IndexedDB: ${workflowToDelete.name}`
        );
        await storageService.deleteWorkflow(workflowToDelete.name);
        console.log("✅ IndexedDB delete completed successfully");
      }

      // Refresh the workflows list
      console.log("🔄 Refreshing workflows list...");
      let workflows;
      if (isAuthenticated && user?.id) {
        workflows = await getAllWorkflowsFromFirestore(user.id);
      } else {
        workflows = await storageService.getAllWorkflows();
      }
      setAvailableWorkflows(workflows);
      console.log("✅ Workflows list refreshed, count:", workflows.length);
      console.log("===== DELETE WORKFLOW DEBUG END =====\n");

      // Clean up ONLY on success
      setShowDeleteConfirmDialog(false);
      setWorkflowToDelete(null);
    } catch (error) {
      console.error("\n❌ ===== DELETE FAILED =====");
      console.error("Error type:", error.constructor.name);
      console.error("Error message:", error.message);
      console.error("Error code:", error.code);
      console.error("Full error:", error);
      console.error("===== DELETE FAILED END =====\n");

      // More detailed error message to user
      let errorMessage = "Failed to delete workflow. ";
      if (error.code === "permission-denied") {
        errorMessage +=
          "Permission denied. Please make sure you're logged in and try again.";
      } else if (error.message.includes("not found")) {
        errorMessage += "Session not found. It may have already been deleted.";
      } else {
        errorMessage += `Error: ${error.message}`;
      }
      alert(errorMessage);

      // Clean up on error too, but keep dialog open if it's a retryable error
      if (error.code !== "permission-denied") {
        setShowDeleteConfirmDialog(false);
        setWorkflowToDelete(null);
      }
    }
  };

  // Load available workflows when modal opens
  useEffect(() => {
    if (showLoadModal) {
      // Load from Firestore if user is authenticated, otherwise from IndexedDB
      const loadWorkflows = async () => {
        try {
          let workflows;
          if (isAuthenticated && user?.id) {
            console.log("📥 Loading workflows from Firestore (authenticated)");
            workflows = await getAllWorkflowsFromFirestore(user.id);
          } else {
            console.log(
              "📥 Loading workflows from IndexedDB (not authenticated)"
            );
            workflows = await storageService.getAllWorkflows();
          }
          console.log("📊 Loaded workflows:", workflows.length);
          setAvailableWorkflows(workflows);
        } catch (error) {
          console.error("Failed to load workflows:", error);
          setAvailableWorkflows([]);
        }
      };
      loadWorkflows();
    } else if (!showLoadModal && nodeCount > 0) {
      // Restore focus to ReactFlow canvas after modal closes
      setTimeout(() => {
        const reactFlowElement = document.querySelector(".react-flow");
        if (reactFlowElement) {
          reactFlowElement.focus();
        }
      }, 300); // Wait for exit animation to complete
    }
  }, [showLoadModal, nodeCount]);

  // Filter and sort workflows
  const filteredAndSortedWorkflows = useMemo(() => {
    let filtered = availableWorkflows.filter((workflow) =>
      workflow.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort workflows
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "books":
          return (b.booksCount || 0) - (a.booksCount || 0);
        case "verses":
          return (b.versesCount || 0) - (a.versesCount || 0);
        case "recent":
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return filtered;
  }, [availableWorkflows, searchTerm, sortBy]);

  // Create unified modal close handler
  const handleCloseLoadModal = () => {
    setShowLoadModal(false);
    setSearchTerm("");
    setSortBy("recent");
    // Ensure any open sub-dialogs are closed
    if (showDeleteConfirmDialog) {
      setShowDeleteConfirmDialog(false);
      setWorkflowToDelete(null);
    }
  };

  // Reset search when modal closes
  useEffect(() => {
    if (!showLoadModal) {
      setSearchTerm("");
      setSortBy("recent");
    }
  }, [showLoadModal]);

  // Handle new exploration with autosave
  const handleNewExploration = async () => {
    setShowNewConfirmDialog(false);

    // Reset workflow - autosave happens automatically when there are changes
    resetWorkflow();

    // Show welcome screen for new exploration
    setShowWelcome(true);
  };

  return (
    <div className="w-full h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Video Recording Buttons - Show on left when no nodes */}
      {nodeCount === 0 &&
        featureFlags.videoRecording &&
        VideoRecordingTrigger &&
        VideoStorageTrigger && (
          <div className="absolute top-2 left-2 z-30 flex items-center gap-2">
            <VideoRecordingTrigger
              onOpenRecorder={() => setIsVideoRecorderOpen(true)}
            />
            <VideoStorageTrigger />
          </div>
        )}

      {/* Navigation Bar - Only show when exploring */}
      {!showWelcome && nodeCount > 0 && (
        <div className="absolute right-2 sm:right-4 top-1 z-30 flex items-center gap-2">
          {/* Video Recording Buttons when exploring */}
          {featureFlags.videoRecording && VideoRecordingTrigger && (
            <VideoRecordingTrigger
              onOpenRecorder={() => setIsVideoRecorderOpen(true)}
            />
          )}
          {featureFlags.videoRecording && VideoStorageTrigger && (
            <VideoStorageTrigger />
          )}
          <button
            onClick={() => {
              if (nodeCount > 0) {
                setShowNewConfirmDialog(true);
              } else {
                handleNewExploration();
              }
            }}
            className="flex items-center justify-center w-10 h-10 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative group">
            <BookPlus size={18} />
            <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
              New Explorations
            </div>
          </button>

          {!isAuthenticated && (
            <button
              onClick={() => setShowLoginModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors -ml-2">
              <User size={18} />
              <span className="text-sm font-medium">Sign In</span>
            </button>
          )}

          {isAuthenticated && (
            <button
              onClick={() => setShowLoadModal(true)}
              className="flex items-center justify-center w-10 h-10 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative group">
              <FolderOpen size={18} />
              <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Your Explorations
              </div>
            </button>
          )}

          {/* User Profile in Navbar - When exploring */}
          {isAuthenticated && user && (
            <div className="relative profile-dropdown-container ml-2">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center gap-2 hover:bg-gray-100 rounded-full px-2 py-1 transition-colors">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <span className="text-sm font-medium text-gray-900 hidden sm:block">
                  {user.name?.split(" ")[0]}
                </span>
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      setShowLogoutModal(true);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                    <LogOut size={16} className="text-gray-600" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Optimized Top Stats Bar */}
      {showStats && (
        <div className="absolute top-0 left-0 right-0 z-20 bg-white/95 backdrop-blur-lg shadow-md border-b border-gray-200/50 px-2 sm:px-4 py-3">
          <div className="relative flex items-center justify-between text-sm sm:text-base">
            {/* Left - App title */}
            <div className="flex items-center">
              <div className="mr-1.5 sm:mr-2.5">
                <img
                  src="/bibecheck-logo-big.png"
                  alt="BibeCheck"
                  className="w-6 h-6"
                />
              </div>
              <span className="font-semibold text-gray-800 text-sm sm:text-base hidden sm:inline">
                BibeCheck
              </span>
            </div>

            {/* Center - Clickable Stats (Absolutely Centered) */}
            {featureFlags.biblicalExplorationProgress && (
              <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center space-x-2 sm:space-x-3.5">
                <button
                  onClick={() => setShowProgressModal(true)}
                  className="flex items-center space-x-1 sm:space-x-1.5 hover:bg-amber-50 rounded-md px-1.5 sm:px-2 py-1 transition-colors cursor-pointer">
                  <span className="text-amber-600 text-sm sm:text-base">
                    📚
                  </span>
                  <span className="font-bold text-amber-700 text-base sm:text-lg">
                    {booksCount}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">
                    Books
                  </span>
                </button>

                <button
                  onClick={() => setShowProgressModal(true)}
                  className="flex items-center space-x-1 sm:space-x-1.5 hover:bg-blue-50 rounded-md px-1.5 sm:px-2 py-1 transition-colors cursor-pointer">
                  <span className="text-blue-600 text-sm sm:text-base">📖</span>
                  <span className="font-bold text-blue-700 text-base sm:text-lg">
                    {versesCount}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">
                    Verses
                  </span>
                </button>
              </div>
            )}

            {/* Right - Actions */}
            <div className="flex items-center space-x-2.5">
              {biblicalMetrics.currentlyExploring && (
                <div className="flex items-center space-x-1.5">
                  <span className="text-purple-600 text-sm">🔎</span>
                  <span className="font-medium text-purple-700 truncate max-w-24">
                    {biblicalMetrics.currentlyExploring}
                  </span>
                </div>
              )}

              {displayConfig.showTopics &&
                biblicalMetrics.topicsDiscovered.size > 0 && (
                  <div className="flex items-center space-x-1.5">
                    <span className="text-green-600 text-sm">💡</span>
                    <span className="font-bold text-green-700">
                      {biblicalMetrics.topicsDiscovered.size}
                    </span>
                    <span className="text-xs text-gray-600">Topics</span>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Top Right Navigation - Welcome Screen (Not Authenticated) */}
      <AnimatePresence>
        {nodeCount === 0 &&
          showWelcome &&
          !isAuthenticated &&
          !showLoginModal &&
          !isLoadingWorkflow && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="absolute top-4 right-4 z-20 flex items-center gap-3">
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 shadow-md hover:shadow-lg transition-all duration-200 font-medium">
                <User size={16} />
                <span>Sign In</span>
              </button>
            </motion.div>
          )}
      </AnimatePresence>

      {/* Top Right Navigation - Welcome Screen (Authenticated) */}
      {nodeCount === 0 && showWelcome && isAuthenticated && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ delay: 1.0, duration: 0.5 }}
          className="absolute top-4 right-4 z-20 flex items-center gap-3">
          <button
            onClick={() => setShowLoadModal(true)}
            className="flex items-center justify-center w-10 h-10 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 text-gray-700 hover:text-blue-700 shadow-sm relative group">
            <FolderOpen size={18} />
            <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
              Your Explorations
            </div>
          </button>

          {/* User Profile Section - After Folder Icon */}
          {user && (
            <div className="relative profile-dropdown-container">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-2 shadow-lg border border-white/20 flex items-center gap-2 hover:bg-white transition-colors">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-6 h-6 rounded-full"
                  />
                )}
                <span className="text-sm font-medium text-gray-900 hidden sm:block">
                  {user.name?.split(" ")[0]}
                </span>
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      setShowLogoutModal(true);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                    <LogOut size={16} className="text-gray-600" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Welcome Screen with Enhanced Animation */}
      <AnimatePresence>
        {nodeCount === 0 && showWelcome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 overflow-y-auto overflow-x-hidden z-10">
            <div className="relative min-h-screen w-full bg-gradient-to-br from-blue-50 via-violet-50 to-indigo-50">
              {/* Enhanced Animated Background Orbs - Ultra smooth with heavy blur */}
              <div className="absolute inset-0 overflow-hidden">
                <style>{`
                  @keyframes blob-float-1 {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50% { transform: translate(50px, -30px) scale(1.1); }
                  }
                  @keyframes blob-float-2 {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50% { transform: translate(-40px, 40px) scale(1.15); }
                  }
                  @keyframes blob-float-3 {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    50% { transform: translate(30px, 50px) scale(1.2); }
                  }
                `}</style>
                <div
                  className="absolute -top-1/2 -right-1/2 w-full h-full rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.15) 50%, rgba(96, 165, 250, 0.1) 100%)',
                    filter: 'blur(100px)',
                    animation: 'blob-float-1 20s ease-in-out infinite',
                    willChange: 'transform'
                  }}
                />
                <div
                  className="absolute -bottom-1/2 -left-1/2 w-full h-full rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(147, 51, 234, 0.13) 0%, rgba(96, 165, 250, 0.1) 50%, rgba(147, 51, 234, 0.05) 100%)',
                    filter: 'blur(100px)',
                    animation: 'blob-float-2 25s ease-in-out infinite',
                    animationDelay: '1s',
                    willChange: 'transform'
                  }}
                />
                <div
                  className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(147, 51, 234, 0.14) 0%, rgba(147, 51, 234, 0.1) 50%, rgba(96, 165, 250, 0.04) 100%)',
                    filter: 'blur(100px)',
                    animation: 'blob-float-3 30s ease-in-out infinite',
                    animationDelay: '2s',
                    willChange: 'transform'
                  }}
                />
              </div>

              <div className="relative min-h-full flex flex-col items-center justify-start pt-32 pb-0">
                {/* Transform Demo Flow - Background decoration - Only show on welcome screen */}
                {/* {!isLoading && nodeCount === 0 && <TransformDemoFlow />} */}

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-center w-full max-w-[95vw] sm:max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl px-6 relative z-20">
                  {/* Icon with Simple Fade animation */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      delay: 0.1,
                      duration: 0.8,
                    }}
                    className="mb-6">
                    <div className="pl-3 pr-4 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-lg mx-auto w-fit mb-4">
                      <RotatedBookIcon size={48} className="text-white" />
                    </div>
                  </motion.div>

                  {/* Title with character stagger animation */}
                  <motion.h1
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                    className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-0 overflow-visible">
                    <motion.span
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}>
                      {getHeadline()}
                    </motion.span>
                    <motion.span
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                      className="block mt-0 pb-0 leading-relaxed sm:whitespace-nowrap">
                      {/* Optimized gradient animation using CSS */}
                      <style>{`
                        @keyframes gradient-shift {
                          0%, 100% {
                            background-position: 0% center;
                          }
                          50% {
                            background-position: 100% center;
                          }
                        }
                      `}</style>
                      <span
                        style={{
                          backgroundImage: "linear-gradient(90deg, rgb(37, 99, 235), rgb(147, 51, 234), rgb(37, 99, 235))",
                          backgroundSize: "200% auto",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: "gradient-shift 5s linear infinite",
                          willChange: "background-position"
                        }}>
                        {getSubheadline()}
                      </span>
                    </motion.span>
                  </motion.h1>

                  {/* Input field with glow effect */}
                  <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      delay: 0.9,
                      duration: 0.5,
                      type: "spring",
                      stiffness: 100,
                    }}>
                    <InputField
                      onSubmit={handleQuestionSubmitWithCheck}
                      isLoading={isLoading}
                      onLoadDefaultWorkflow={loadWorkflowWithCheck}
                      showLoginModal={showLoginModal}
                      setShowLoginModal={setShowLoginModal}
                    />
                  </motion.div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg max-w-md mx-auto">
                      {error}
                    </motion.div>
                  )}

                  {/* Transform Demo Flow - Centered with Scroll Animation */}
                  {!isLoading && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="mt-32 mb-8 flex justify-center">
                      <TransformDemoFlow />
                    </motion.div>
                  )}

                  {/* Start Your Bible Journey Section */}
                  {!isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.6 }}
                      className="max-w-4xl mx-auto mt-8 mb-8">
                      <motion.h3
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
                        Start Your Bible Journey
                      </motion.h3>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="text-center text-gray-600 mb-12 text-xl">
                        Making Scripture accessible to everyone. Just bring your
                        curiosity.
                      </motion.p>
                      <div className="grid md:grid-cols-3 gap-8 text-center">
                        {getJourneyCardsArray().map((card, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30, scale: 0.9 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.5,
                              delay: 0.4 + index * 0.15,
                              type: "spring",
                              stiffness: 100,
                            }}
                            whileHover={{
                              y: -8,
                              scale: 1.02,
                              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                              transition: { duration: 0.2 },
                            }}
                            style={{ willChange: "transform" }}
                            className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 cursor-default">
                            <motion.div
                              initial={{ opacity: 0 }}
                              whileInView={{ opacity: 1 }}
                              viewport={{ once: true }}
                              transition={{
                                delay: 0.5 + index * 0.15,
                                duration: 0.3,
                              }}
                              className="text-5xl mb-4 inline-block">
                              {card.icon}
                            </motion.div>
                            <h4 className="font-bold text-gray-900 mb-2 text-xl">
                              {card.title}
                            </h4>
                            <p className="text-base text-gray-600">
                              {card.description}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Video Demo - Scroll Reveal */}
                  {!isLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                      className="mt-16">
                      <VideoDemo delay={0} />
                    </motion.div>
                  )}
                </motion.div>

                {/* Founder Story - Scroll Reveal */}
                {!isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="w-full mt-8 relative z-20">
                    <FounderStory delay={0} />
                  </motion.div>
                )}

                {/* How It Works - COMMENTED OUT (Contains Start Your Bible Journey at bottom) */}
                {/* {!isLoading && (
                <div className="w-full">
                  <HowItWorks delay={1.2} />
                </div>
              )} */}

                {/* Footer - Fade In */}
                {!isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="w-full">
                    <Footer />
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced ReactFlow Canvas with optimized props */}
      <div
        className={`w-full h-full ${showStats && !showWelcome ? "pt-12" : ""}`}>
        <ReactFlow
          nodes={nodes}
          edges={edgesWithType}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onSelectionChange={onSelectionChange}
          onNodeClick={onNodeClick}
          onInit={setReactFlowInstance}
          onMove={onViewportChange}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={{ type: edgeType }}
          connectionLineType={ConnectionLineType.SmoothStep}
          defaultViewport={{ x: 0, y: 0, zoom: 1.0 }}
          fitView={false}
          fitViewOptions={{
            padding: 0.3,
            includeHiddenNodes: false,
            maxZoom: 1.0,
            duration: 800,
          }}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
          minZoom={0.2}
          maxZoom={1.5}
          panOnScroll
          selectionOnDrag={false}
          panOnDrag={true}
          selectNodesOnDrag={false}
          deleteKeyCode={null}
          multiSelectionKeyCode={null}
          preventScrolling={false}
          snapToGrid={false}
          snapGrid={[15, 15]}
          nodesDraggable={true}
          nodesConnectable={true}
          elementsSelectable={true}>
          <Background
            color="#e2e8f0"
            gap={24}
            size={2}
            style={{
              backgroundColor: "rgba(248, 250, 252, 0.5)",
            }}
          />
          {nodeCount > 0 && (
            <CustomControls
              showStats={showStats}
              collapseAllNodes={collapseAllNodes}
              expandAllNodes={expandAllNodes}
              getExpandedNodesCount={getExpandedNodesCount}
              nodes={nodes}
              featureFlags={featureFlags}
              edgeType={localEdgeType}
              setEdgeType={setLocalEdgeType}
            />
          )}

          {/* Minimap with toggle functionality */}
          {nodeCount > 0 && isMinimapVisible && (
            <>
              <MiniMap
                position="bottom-left"
                nodeColor={(node) => {
                  if (node.data?.isFirstNode) return "#d4af37"; // Golden color for first node
                  if (node.data?.isLastNode) return "#10b981"; // Emerald-500 for last node
                  if (node.data?.isSummary) return "#fcd34d"; // Amber-300 for summary nodes (lighter)
                  return "#d1d5db"; // Gray-300 for regular nodes - much lighter gray
                }}
                nodeStrokeColor={(node) => {
                  if (node.data?.isFirstNode) return "#b8961f"; // Darker golden stroke for first node
                  if (node.data?.isLastNode) return "#059669"; // Darker emerald stroke for last node
                  if (node.data?.isSummary) return "#f59e0b"; // Amber-500 stroke for summary
                  return "#e5e7eb"; // Gray-200 for even subtler borders
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
              />

              <button
                onClick={async () => {
                  setIsMinimapVisible(false);
                  try {
                    await storageService.updateMinimapVisibility(false);
                  } catch (error) {
                    console.error("Failed to save minimap preference:", error);
                  }
                }}
                className="fixed bottom-[134px] left-[184px] z-[1001] w-6 h-6 bg-white/80 hover:bg-white border border-gray-300 rounded-full flex items-center justify-center transition-colors shadow-sm"
                title="Minimize map">
                <ChevronDown size={12} className="text-gray-600" />
              </button>
            </>
          )}

          {/* Minimap Toggle Button - Map Icon when minimized */}
          {nodeCount > 0 && !isMinimapVisible && (
            <button
              onClick={async () => {
                setIsMinimapVisible(true);
                try {
                  await storageService.updateMinimapVisibility(true);
                } catch (error) {
                  console.error("Failed to save minimap preference:", error);
                }
              }}
              className="fixed bottom-4 left-4 z-50 w-10 h-10 bg-white/95 backdrop-blur-sm border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
              title="Show map">
              <Map size={16} className="text-gray-600" />
            </button>
          )}
        </ReactFlow>
      </div>

      {/* Workflow Loading Overlay */}
      {isLoadingWorkflow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="text-center">
            {/* Spinning loader */}
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            {/* Loading text */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg font-medium text-gray-700 mb-2">
              Loading exploration...
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-sm text-gray-500">
              Preparing your biblical journey
            </motion.p>
          </motion.div>
        </motion.div>
      )}

      {/* Global Styles for Enhanced Visuals */}
      <style jsx="true" global="true">{`
        .react-flow__node[data-id^="response-"] {
          background: transparent !important;
          border: none !important;
        }

        .react-flow__edge-path {
          stroke: #d4af37;
          stroke-width: 2;
          filter: drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3));
        }

        .react-flow__edge.selected .react-flow__edge-path {
          stroke: #b45309;
          stroke-width: 3;
        }

        .react-flow__connection-line {
          stroke: #d4af37;
          stroke-width: 2;
          stroke-dasharray: 5;
        }

        .react-flow__controls {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: blur(8px);
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        .react-flow__controls button {
          background: transparent !important;
          border: none !important;
          border-bottom: 1px solid #e2e8f0 !important;
          color: #374151 !important;
          transition: all 0.2s ease !important;
        }

        .react-flow__controls button:hover {
          background: #f3f4f6 !important;
          color: #1f2937 !important;
        }

        .react-flow__controls button[title="toggle interactivity"],
        .react-flow__controls button[title="Toggle Interactivity"],
        .react-flow__controls button[aria-label="toggle interactivity"],
        .react-flow__controls button[aria-label="Toggle Interactivity"] {
          display: none !important;
        }

        .react-flow__minimap {
          background: rgba(255, 255, 255, 0.95) !important;
          backdrop-filter: blur(8px);
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          position: fixed !important;
          bottom: 16px !important;
          left: 16px !important;
          z-index: 1000 !important;
          width: 200px !important;
          height: 150px !important;
          transform: none !important;
        }

        .react-flow__panel.react-flow__minimap {
          position: fixed !important;
          bottom: 16px !important;
          left: 16px !important;
          top: auto !important;
          right: auto !important;
          transform: none !important;
        }
      `}</style>

      {/* Enhanced Load Workflow Modal - 10x Better */}
      <AnimatePresence
        onExitComplete={() => {
          // Force focus back to canvas after modal animations complete
          const reactFlowElement = document.querySelector(".react-flow");
          if (reactFlowElement) {
            reactFlowElement.focus();
          }
        }}>
        {showLoadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, pointerEvents: "none" }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={handleCloseLoadModal}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              tabIndex={-1}>
              {/* Enhanced Header - Light Style */}
              <div className="bg-white px-8 py-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="rounded-lg">
                      <img
                        src="/bibecheck-logo-big.png"
                        alt="BibeCheck"
                        className="w-9 h-9"
                      />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-1">
                        Your Biblical Explorations
                      </h2>
                      <p className="text-gray-500 text-sm">
                        Choose from your saved journeys through scripture
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseLoadModal}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                    <X size={20} />
                  </button>
                </div>

                {/* Search and Filter Bar */}
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                      <Search size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search your explorations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                    />
                  </div>

                  {/* Sort Options */}
                  <div className="flex gap-2">
                    <div className="relative">
                      <select
                        value={sortBy}
                        onChange={async (e) => {
                          const newSortBy = e.target.value;
                          setSortBy(newSortBy);
                          try {
                            await storageService.updateSearchOptions({
                              sortBy: newSortBy,
                            });
                          } catch (error) {
                            console.error(
                              "Failed to save sort preference:",
                              error
                            );
                          }
                        }}
                        className="pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500 focus:bg-white transition-all appearance-none">
                        <option value="recent">Most Recent</option>
                        <option value="name">Name A-Z</option>
                        {featureFlags.biblicalExplorationProgress && (
                          <>
                            <option value="books">Most Books</option>
                            <option value="verses">Most Verses</option>
                          </>
                        )}
                      </select>
                      <ChevronDown
                        size={18}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                      <button
                        onClick={async () => {
                          setViewMode("list");
                          try {
                            await storageService.updateSearchOptions({
                              viewMode: "list",
                            });
                          } catch (error) {
                            console.error(
                              "Failed to save view mode preference:",
                              error
                            );
                          }
                        }}
                        className={`p-3 transition-colors ${
                          viewMode === "list"
                            ? "bg-blue-500 text-white"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                        }`}>
                        <List size={18} />
                      </button>
                      <button
                        onClick={async () => {
                          setViewMode("grid");
                          try {
                            await storageService.updateSearchOptions({
                              viewMode: "grid",
                            });
                          } catch (error) {
                            console.error(
                              "Failed to save view mode preference:",
                              error
                            );
                          }
                        }}
                        className={`p-3 transition-colors ${
                          viewMode === "grid"
                            ? "bg-blue-500 text-white"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                        }`}>
                        <Grid3X3 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {filteredAndSortedWorkflows.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center">
                    {searchTerm ? (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <Search size={32} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                          No explorations found
                        </h3>
                        <p className="text-gray-500 mb-4">
                          No explorations match "{searchTerm}"
                        </p>
                        <button
                          onClick={() => setSearchTerm("")}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                          Clear search
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        {/* <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <img
                            src="/icons/icon-72x72.png"
                            alt="BibeCheck"
                            className="w-8 h-8"
                            style={{ filter: "brightness(0.6)" }}
                          />
                        </div> */}
                        <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-8">
                          No saved explorations
                        </h3>
                        <p className="text-gray-500 mb-4">
                          Start your biblical journey to create explorations
                        </p>
                        <button
                          onClick={handleCloseLoadModal}
                          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium shadow-lg">
                          Begin Exploring
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Results Summary */}
                    <div className="px-8 py-4 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-gray-600">
                            {filteredAndSortedWorkflows.length} exploration
                            {filteredAndSortedWorkflows.length !== 1 ? "s" : ""}
                            {searchTerm && ` matching "${searchTerm}"`}
                          </span>
                          {(currentWorkflowName ||
                            currentFirestoreSessionId) && (
                            <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-full">
                              <Lock size={14} className="text-blue-600" />
                              <span className="text-blue-700 text-sm font-medium">
                                Current:{" "}
                                {(() => {
                                  // Find the current workflow from the list
                                  if (currentFirestoreSessionId) {
                                    const current =
                                      filteredAndSortedWorkflows.find(
                                        (w) =>
                                          w.id === currentFirestoreSessionId
                                      );
                                    return (
                                      current?.name ||
                                      currentWorkflowName ||
                                      "Unknown"
                                    );
                                  }
                                  return currentWorkflowName;
                                })()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Explorations List/Grid */}
                    <div
                      className={`flex-1 overflow-y-auto p-6 ${
                        viewMode === "grid"
                          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                          : "space-y-4"
                      }`}>
                      {filteredAndSortedWorkflows.map((workflow) => {
                        // Check if this is the current workflow
                        // For Firestore: compare session IDs
                        // For IndexedDB: compare workflow names
                        const isFirestoreWorkflow =
                          workflow.id?.startsWith("session-");
                        const isCurrentWorkflow = isFirestoreWorkflow
                          ? currentFirestoreSessionId === workflow.id
                          : currentWorkflowName === workflow.name;

                        // Calculate biblical metadata if not present
                        let booksCount = workflow.booksCount || 0;
                        let versesCount = workflow.versesCount || 0;

                        if (
                          booksCount === 0 &&
                          versesCount === 0 &&
                          workflow.nodes
                        ) {
                          const allBooks = new Set();
                          const allVerses = new Set();

                          workflow.nodes.forEach((node) => {
                            if (node.data?.booksMentioned) {
                              node.data.booksMentioned.forEach((book) =>
                                allBooks.add(book)
                              );
                            }
                            if (node.data?.versesReferenced) {
                              node.data.versesReferenced.forEach((verse) =>
                                allVerses.add(verse)
                              );
                            }
                          });

                          booksCount = allBooks.size;
                          versesCount = allVerses.size;
                        }

                        return (
                          <motion.div
                            key={workflow.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            className={`relative group bg-white transition-all duration-200 ${
                              viewMode === "grid"
                                ? "rounded-xl border-2 hover:shadow-xl"
                                : "rounded-lg border border-gray-200"
                            } ${
                              isCurrentWorkflow
                                ? viewMode === "grid"
                                  ? "border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg"
                                  : "border-blue-200 bg-blue-50/30"
                                : viewMode === "grid"
                                ? "border-gray-200 hover:border-blue-300 hover:shadow-lg hover:-translate-y-1"
                                : "hover:border-gray-300 hover:bg-gray-50"
                            } ${viewMode === "grid" ? "p-4" : "px-4 py-3"}`}>
                            {/* Exploration Content */}
                            <button
                              onClick={() =>
                                handleLoadWorkflow(workflow.id, workflow.name)
                              }
                              className="w-full text-left">
                              {/* Header */}
                              <div
                                className={`${
                                  viewMode === "list"
                                    ? "flex items-center justify-between"
                                    : "flex items-start justify-between mb-3"
                                }`}>
                                <div className="flex-1">
                                  <div
                                    className={`${
                                      viewMode === "list"
                                        ? "flex items-center gap-4"
                                        : "block"
                                    }`}>
                                    <h3
                                      className={`${
                                        viewMode === "list"
                                          ? "font-semibold text-base line-clamp-1"
                                          : "font-bold text-base mb-1.5 line-clamp-2"
                                      } ${
                                        isCurrentWorkflow
                                          ? "text-blue-700"
                                          : "text-gray-900"
                                      }`}>
                                      {workflow.name}
                                    </h3>

                                    {/* Date */}
                                    <div
                                      className={`flex items-center gap-2 ${
                                        viewMode === "list" ? "" : "mb-2"
                                      }`}>
                                      <Calendar
                                        size={12}
                                        className="text-gray-400"
                                      />
                                      <span
                                        className={`${
                                          viewMode === "list"
                                            ? "text-xs"
                                            : "text-sm"
                                        } text-gray-500`}>
                                        {(() => {
                                          const date = new Date(
                                            workflow.createdAt
                                          );
                                          const now = new Date();
                                          const diffTime = Math.abs(now - date);
                                          const diffDays = Math.ceil(
                                            diffTime / (1000 * 60 * 60 * 24)
                                          );

                                          if (diffDays === 1) return "Today";
                                          if (diffDays === 2)
                                            return "Yesterday";
                                          if (diffDays <= 7)
                                            return `${diffDays - 1} days ago`;
                                          if (diffDays <= 30)
                                            return `${Math.floor(
                                              (diffDays - 1) / 7
                                            )} week${
                                              Math.floor((diffDays - 1) / 7) > 1
                                                ? "s"
                                                : ""
                                            } ago`;

                                          return date.toLocaleDateString(
                                            "en-US",
                                            {
                                              month: "short",
                                              day: "numeric",
                                              year:
                                                date.getFullYear() !==
                                                now.getFullYear()
                                                  ? "numeric"
                                                  : undefined,
                                            }
                                          );
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Status Badge - Only show in grid view */}
                                {isCurrentWorkflow && viewMode === "grid" && (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 rounded-full">
                                    <Lock size={12} className="text-blue-600" />
                                    <span className="text-xs font-medium text-blue-700">
                                      Current
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Responsive Stats Layout */}
                              {featureFlags.biblicalExplorationProgress && (
                                <>
                                  <div
                                    className={`mb-3 ${
                                      viewMode === "grid"
                                        ? "grid grid-cols-2 gap-2"
                                        : "flex items-center justify-between"
                                    }`}>
                                    <div className="flex items-center gap-1.5">
                                      <div className="w-6 h-6 bg-gradient-to-br from-amber-100 to-amber-200 rounded flex items-center justify-center">
                                        <span className="text-xs">📚</span>
                                      </div>
                                      <span className="font-bold text-amber-700 text-sm">
                                        {booksCount}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        Books
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                      <div className="w-6 h-6 bg-gradient-to-br from-blue-100 to-blue-200 rounded flex items-center justify-center">
                                        <span className="text-xs">📖</span>
                                      </div>
                                      <span className="font-bold text-blue-700 text-sm">
                                        {versesCount}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        Verses
                                      </span>
                                    </div>

                                    {(booksCount > 0 || versesCount > 0) && (
                                      <div
                                        className={`flex items-center gap-1.5 ${
                                          viewMode === "grid"
                                            ? "col-span-2 justify-center"
                                            : ""
                                        }`}>
                                        <span className="font-bold text-green-700 text-sm">
                                          {Math.round((booksCount / 66) * 100)}%
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          Complete
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Compact Progress Bar */}
                                  {booksCount > 0 && (
                                    <div className="mb-3">
                                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                                        <div
                                          className="bg-gradient-to-r from-amber-400 to-amber-600 h-1.5 rounded-full transition-all duration-500"
                                          style={{
                                            width: `${Math.min(
                                              (booksCount / 66) * 100,
                                              100
                                            )}%`,
                                          }}></div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* Action indicator for list view */}
                              {viewMode === "list" && isCurrentWorkflow && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full ml-auto"></div>
                              )}

                              {/* Compact Load Button for grid view */}
                              {viewMode === "grid" && (
                                <>
                                  {isCurrentWorkflow ? (
                                    <div className="px-3 py-1.5 rounded text-center text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                                      ● Active
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 w-full">
                                      <div className="flex-1 px-3 py-1.5 rounded text-center text-xs font-medium bg-white text-gray-600 border border-gray-300 group-hover:bg-gray-50 group-hover:border-gray-400 transition-colors">
                                        Continue
                                      </div>
                                      <button
                                        onClick={(e) =>
                                          handleDeleteWorkflow(
                                            workflow.id,
                                            workflow.name,
                                            e
                                          )
                                        }
                                        className="px-2 py-1.5 rounded text-xs font-medium border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400 transition-colors"
                                        title="Delete">
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </button>

                            {/* Delete Button - List view only */}
                            {!isCurrentWorkflow && viewMode === "list" && (
                              <button
                                onClick={(e) =>
                                  handleDeleteWorkflow(
                                    workflow.id,
                                    workflow.name,
                                    e
                                  )
                                }
                                className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"
                                title="Delete exploration">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Enhanced Footer */}
              <div className="px-8 py-6 bg-gray-50 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    💡 Tip: Click on any exploration to continue your biblical
                    journey
                  </div>
                  <button
                    onClick={handleCloseLoadModal}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-700">
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Biblical Progress Modal */}
      {featureFlags.biblicalExplorationProgress && (
        <AnimatePresence>
          {showProgressModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={() => setShowProgressModal(false)}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-2xl border border-gray-200/50 p-6 max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto backdrop-blur-lg"
                onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-sm">
                      <img
                        src="/icons/icon-72x72.png"
                        alt="BibeCheck"
                        className="w-5 h-5"
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    </div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                      Biblical Exploration Progress
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowProgressModal(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                    <X size={20} />
                  </button>
                </div>

                {/* Progressive Achievement Section with Enhanced Visual Hierarchy */}
                <ProgressiveAchievement
                  booksCount={booksCount}
                  versesCount={versesCount}
                  books={books}
                />

                {/* Books Progress Section with Enhanced Hierarchy */}
                <div className="mb-8">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    {/* Section Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg flex items-center justify-center">
                          <span className="text-lg">📚</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Books of the Bible
                          </h3>
                          <p className="text-sm text-gray-500">
                            Your exploration across the scriptures
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-amber-700">
                          {booksCount}
                        </div>
                        <div className="text-xs text-gray-500">of 66 books</div>
                      </div>
                    </div>

                    {/* Enhanced Progress Bar */}
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Progress
                        </span>
                        <span className="text-sm font-bold text-amber-700">
                          {Math.round((booksCount / 66) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden shadow-inner">
                        <div
                          className="bg-gradient-to-r from-amber-400 to-amber-600 h-4 rounded-full transition-all duration-700 ease-out shadow-sm"
                          style={{
                            width: `${Math.min((booksCount / 66) * 100, 100)}%`,
                          }}></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>Beginning</span>
                        <span>Complete</span>
                      </div>
                    </div>
                  </div>

                  {/* Books List - Organized by Testament with Enhanced Hierarchy */}
                  {books.length > 0 &&
                    (() => {
                      const { oldTestament, newTestament, other } =
                        categorizeBooks(books);
                      return (
                        <div className="mt-6 space-y-6">
                          {/* Old Testament */}
                          {oldTestament.length > 0 && (
                            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gradient-to-br from-amber-200 to-amber-300 rounded-lg flex items-center justify-center">
                                    <span className="text-sm">📜</span>
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-amber-800">
                                      Old Testament
                                    </h4>
                                    <p className="text-xs text-amber-600">
                                      Foundation of faith
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-amber-700">
                                    {oldTestament.length}
                                  </div>
                                  <div className="text-xs text-amber-600">
                                    of 39
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {oldTestament.map((book, index) => (
                                  <div
                                    key={index}
                                    className="text-sm text-amber-800 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm hover:shadow-md transition-shadow font-medium">
                                    {book}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* New Testament */}
                          {newTestament.length > 0 && (
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-200 to-blue-300 rounded-lg flex items-center justify-center">
                                    <span className="text-sm">✨</span>
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-blue-800">
                                      New Testament
                                    </h4>
                                    <p className="text-xs text-blue-600">
                                      Gospel & teachings
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-blue-700">
                                    {newTestament.length}
                                  </div>
                                  <div className="text-xs text-blue-600">
                                    of 27
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {newTestament.map((book, index) => (
                                  <div
                                    key={index}
                                    className="text-sm text-blue-800 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm hover:shadow-md transition-shadow font-medium">
                                    {book}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Other books (if any) */}
                          {other.length > 0 && (
                            <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-100">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center">
                                    <span className="text-sm">📚</span>
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-gray-800">
                                      Other Books
                                    </h4>
                                    <p className="text-xs text-gray-600">
                                      Additional references
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-gray-700">
                                    {other.length}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    books
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {other.map((book, index) => (
                                  <div
                                    key={index}
                                    className="text-sm text-gray-800 bg-white/80 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm hover:shadow-md transition-shadow font-medium">
                                    {book}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </div>

                {/* Verses Progress Section with Enhanced Hierarchy */}
                <div className="mb-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    {/* Section Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                          <span className="text-lg">📖</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">
                            Verses Referenced
                          </h3>
                          <p className="text-sm text-gray-500">
                            Scripture passages you've explored
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-700">
                          {versesCount}
                        </div>
                        <div className="text-xs text-gray-500">
                          verses found
                        </div>
                      </div>
                    </div>

                    {/* Verses List */}
                    {verses.length > 0 ? (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 max-h-80 overflow-y-auto border border-blue-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {verses.sort().map((verse, index) => (
                            <div
                              key={index}
                              className="group text-sm text-blue-800 bg-white/80 backdrop-blur-sm rounded-lg px-4 py-3 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-blue-100/50 hover:border-blue-200">
                              <div className="font-medium group-hover:text-blue-900 transition-colors">
                                {verse}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <div className="text-4xl mb-2">📖</div>
                        <p className="text-sm">No verses explored yet</p>
                        <p className="text-xs mt-1">
                          Start asking questions to discover scripture!
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowProgressModal(false)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium text-gray-700">
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Recovery Modal */}
      <AnimatePresence>
        {showRecoveryPrompt && recoveryData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="text-center mb-6">
                <div className="mb-3">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">🔄</span>
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Recover Previous Session?
                </h2>
                <p className="text-gray-600">
                  We found an unsaved Bible study session from{" "}
                  <strong>{recoveryData.timeAgo}</strong>
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="font-medium text-gray-800 mb-1">
                  "{recoveryData.title}"
                </div>
                <div className="text-sm text-gray-500">
                  {recoveryData.data?.nodes?.length || 0} responses •{" "}
                  {recoveryData.data?.edges?.length || 0} connections
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={dismissRecovery}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                  Start Fresh
                </button>
                <button
                  onClick={recoverAutosave}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium">
                  Recover Session
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom New Exploration Confirmation Dialog */}
      <AnimatePresence>
        {showNewConfirmDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowNewConfirmDialog(false)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-r from-green-500 to-green-600 rounded-lg">
                  <BookPlus size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    Start New Exploration?
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <p className="text-gray-600 leading-relaxed">
                  {currentWorkflowName ? (
                    <>
                      <span className="font-bold text-gray-800">
                        "{currentWorkflowName}"
                      </span>{" "}
                      will be saved automatically.
                    </>
                  ) : (
                    "Your current work will be saved automatically."
                  )}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowNewConfirmDialog(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700">
                  Cancel
                </button>
                <button
                  onClick={handleNewExploration}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-medium shadow-lg">
                  Start New
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Delete Confirmation Dialog - Without AnimatePresence to prevent nesting issues */}
      {showDeleteConfirmDialog && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, pointerEvents: "none" }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60]"
          onClick={() => {
            setShowDeleteConfirmDialog(false);
            setWorkflowToDelete(null);
          }}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-r from-red-500 to-red-600 rounded-lg">
                <Trash2 size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Delete Exploration?
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-gray-600 leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-gray-800">
                  "{workflowToDelete?.name}"
                </span>
                ? This action cannot be undone.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmDialog(false);
                  setWorkflowToDelete(null);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700">
                Cancel
              </button>
              <button
                onClick={confirmDeleteWorkflow}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all font-medium shadow-lg">
                Delete
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Global Video Recorder - Persistent across layout changes */}
      {featureFlags.videoRecording && VideoRecorder && (
        <VideoRecorder
          isOpen={isVideoRecorderOpen}
          onClose={() => setIsVideoRecorderOpen(false)}
        />
      )}

      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          const wasOnExplorationPage = nodeCount > 0;
          logout();
          setShowLogoutModal(false);
          setShowPaywall(false); // Close paywall after logout

          // Reset checkout states on logout
          setCheckoutLoading(false);
          setIsProcessingCheckout(false);

          // If logging out from exploration page, go back to landing page
          if (wasOnExplorationPage) {
            resetWorkflow();
            setShowWelcome(true);
          }
        }}
        userName={user?.name?.split(" ")[0]}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={async (provider) => {
          const result = await login(provider);
          if (result.success) {
            setShowLoginModal(false);
          }
        }}
      />

      {/* Onboarding Flow */}
      <OnboardingFlow
        isOpen={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
        userName={user?.name}
        userPhoto={user?.picture}
      />

      {/* Transition Screen - Between Onboarding and Paywall */}
      <TransitionScreen
        isOpen={showTransition}
        onContinue={handleTransitionComplete}
        onBack={handleTransitionBack}
        userName={user?.name}
        userPhoto={user?.picture}
        personality={userPersonality}
      />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall && !showOnboarding && !showTransition}
        onSubscribe={handleSubscribe}
        isLoading={checkoutLoading}
        onBack={handlePaywallBack}
        personality={userPersonality}
        onSignOut={() => {
          // Don't close paywall - just show logout modal
          // Paywall will close after successful logout
          setShowLogoutModal(true);
        }}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        userName={user?.name}
        personality={userPersonality}
      />

      {/* Rate Limit Modal */}
      <RateLimitModal
        isOpen={rateLimitError?.isRateLimit === true}
        onClose={clearRateLimitError}
        resetTime={rateLimitError?.resetTime}
        limitType={rateLimitError?.limitType}
      />
    </div>
  );
};

export default FlowCanvas;
